/**
 * useResults — persists calculator outputs to localStorage.
 *
 * Storage safety: localStorage is used intentionally here because this hook
 * stores only NON-SENSITIVE calculator data (macros, TDEE, 1RM, peptide doses
 * the user voluntarily enters into client-side calculators). It contains no
 * auth tokens, no PII, no payment data. If a future calculator stores anything
 * sensitive, migrate that field to the backend instead.
 */
import { useState, useCallback } from 'react';

const STORAGE_KEY = 'ninja_athlete_results';
const MAX_HISTORY = 20;

const getStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const persist = (data) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

export const TOOL_META = {
  protein: { label: 'Protein', unit: 'g/day', color: '#E63946', icon: 'Calculator' },
  tdee: { label: 'TDEE', unit: 'cal/day', color: '#ff6b35', icon: 'Flame' },
  macros: { label: 'Macros', unit: 'cal/day', color: '#00F0FF', icon: 'PieChart' },
  oneRepMax: { label: '1RM', unit: '', color: '#E63946', icon: 'Dumbbell' },
  bodyFat: { label: 'Body Fat', unit: '%', color: '#FFD100', icon: 'Ruler' },
  heartRate: { label: 'HR Zones', unit: 'bpm max', color: '#22c55e', icon: 'Heart' },
  hyroxFinishTime: { label: 'HYROX Finish', unit: '', color: '#e8ff00', icon: 'Zap' },
  peptideDose: { label: 'Peptide Dose', unit: '', color: '#E63946', icon: 'Syringe' },
  stationSplit: { label: 'Station Split', unit: '', color: '#e8ff00', icon: 'Crosshair' },
  recoveryStack: { label: 'Recovery Stack', unit: '/100', color: '#00F0FF', icon: 'Shield' },
  reconstitution: { label: 'Reconstitution', unit: '', color: '#E63946', icon: 'Beaker' },
  bodyRecomp: { label: 'Body Recomp', unit: '', color: '#00F0FF', icon: 'Activity' },
  peptideCycle: { label: 'Peptide Cycles', unit: '', color: '#E63946', icon: 'Repeat' },
  peptideStack: { label: 'Peptide Stack', unit: '', color: '#E63946', icon: 'FlaskConical' },
  biohackingScore: { label: 'Biohacking Score', unit: '/100', color: '#E63946', icon: 'Shield' },
};

export function useResults() {
  const [data, setData] = useState(getStored);

  const saveResult = useCallback((toolKey, result) => {
    const entry = { ...result, date: new Date().toISOString().slice(0, 10), ts: Date.now() };
    const updated = { ...getStored() };
    if (!updated[toolKey]) updated[toolKey] = { history: [] };
    updated[toolKey].history.unshift(entry);
    if (updated[toolKey].history.length > MAX_HISTORY) {
      updated[toolKey].history = updated[toolKey].history.slice(0, MAX_HISTORY);
    }
    persist(updated);
    setData(updated);
    return true;
  }, []);

  const getLatest = useCallback((toolKey) => {
    const d = getStored();
    return d[toolKey]?.history?.[0] || null;
  }, []);

  const getHistory = useCallback((toolKey) => {
    const d = getStored();
    return d[toolKey]?.history || [];
  }, []);

  const getAllLatest = useCallback(() => {
    const d = getStored();
    const out = {};
    Object.keys(d).forEach(k => {
      if (d[k]?.history?.length) out[k] = d[k].history[0];
    });
    return out;
  }, []);

  const clearTool = useCallback((toolKey) => {
    const updated = { ...getStored() };
    delete updated[toolKey];
    persist(updated);
    setData(updated);
  }, []);

  const clearAll = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setData({});
  }, []);

  const hasAnyResults = useCallback(() => {
    const d = getStored();
    return Object.keys(d).some(k => d[k]?.history?.length > 0);
  }, []);

  return { data, saveResult, getLatest, getHistory, getAllLatest, clearTool, clearAll, hasAnyResults };
}
